import jsPDF from 'jspdf';
import axiosInstance from '../../../../axiosInstance';

// Fetch shop details
const fetchShopDetails = async () => {
    try {
        const response = await axiosInstance.get('/shop-details');
        if (response.status === 200) {
            return response.data.shop;
        } else {
            throw new Error('Error fetching shop details');
        }
    } catch (error) {
        console.log('Error:', error);
        return null;
    }
};

// Convert image to base64
const loadImageAsBase64 = (src) => {
    return new Promise((resolve, reject) => {
        const img = new Image();
        img.crossOrigin = 'Anonymous';
        img.src = src;
        img.onload = () => {
            const canvas = document.createElement('canvas');
            canvas.width = img.width;
            canvas.height = img.height;
            const ctx = canvas.getContext('2d');
            ctx.drawImage(img, 0, 0);
            resolve(canvas.toDataURL('image/png'));
        };
        img.onerror = reject;
    });
};

// Generate invoice PDF
const generateInvoice = async (saleDetails, items, totalQuantity, totalPrice, totalDiscount) => {
    const shop = await fetchShopDetails();
    if (!shop) {
        console.error('Shop details could not be fetched');
        return;
    }

    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const leftX = 15;
    const rightX = 195;
    let y = 20;

    // === Load Shop Logo and Sell Smart Logo ===
    let shopLogo = '';
    let sellSmartLogo = '';
    try {
        if (shop.logo) {
            shopLogo = await loadImageAsBase64(shop.logo);
        }
        sellSmartLogo = await loadImageAsBase64('/images/sell-smart-logo.png');
    } catch (err) {
        console.warn('Failed to load logo images:', err);
    }

    // === Shop Logo and Info ===
    if (shopLogo) {
        doc.addImage(shopLogo, 'PNG', leftX, y - 5, 25, 25);
        doc.setFont("helvetica", "bold").setFontSize(16);
        doc.text(shop.name || 'Business Name', leftX + 30, y);
        doc.setFontSize(10).setFont("helvetica", "normal");
        if (shop.location !== null) {
            y += 6;
            doc.text(shop.location || 'N/A', leftX + 30, y);
        }
        if (shop.email !== null) {
            y += 5;
            doc.text(shop.email || ' ', leftX + 30, y);
        }
        if (shop.phone !== null) {
            y += 5;
            doc.text(shop.phone || 'N/A', leftX + 30, y);
        }
    } else {
        doc.setFont("helvetica", "bold").setFontSize(16);
        doc.text(shop.name || 'Business Name', leftX, y);
        doc.setFontSize(10).setFont("helvetica", "normal");
        y += 6;
        doc.text(shop.location || ' ', leftX, y);
        y += 5;
        doc.text(shop.email || ' ', leftX, y);
        y += 5;
        doc.text(shop.phone || ' ', leftX, y);
    }

    // === Invoice Info Right ===
    y = 20;
    doc.setFontSize(12).setFont("helvetica", "bold");
    doc.text(
        saleDetails?.type === 'invoice'
            ? 'INVOICE'
            : saleDetails?.type === 'proforma'
                ? 'PROFORMA INVOICE'
                : 'RECEIPT',
        rightX,
        y,
        { align: 'right' }
    );
    doc.setFontSize(10).setFont("helvetica", "normal");
    y += 6;
    doc.text(
        saleDetails?.type === 'invoice'
            ? `Invoice No: ${saleDetails?.invoice_number}`
            : saleDetails?.type === 'proforma'
                ? `Proforma Invoice No: ${saleDetails?.invoice_number}`
                : `Receipt No: ${saleDetails?.receipt_number}`,
        rightX,
        y,
        { align: 'right' }
    );
    if (saleDetails?.type === 'receipt') {
        y += 5;
        doc.text(`Date: ${saleDetails?.payment_date || 'N/A'}`, rightX, y, { align: 'right' });
    }
    y += 5;
    doc.text(`Due Date: ${saleDetails?.type === 'invoice' ? saleDetails?.due_date : saleDetails?.payment_date || 'N/A'}`, rightX, y, { align: 'right' });

    // === Customer Info ===
    if (saleDetails?.customer !== null) {
        y += 15;
        doc.setFont("helvetica", "bold");
        doc.text('BILL TO:', leftX, y);
        y += 6;
        doc.setFont("helvetica", "normal");
        doc.text(saleDetails?.customer?.name || '', leftX, y);
        y += 5;
        doc.text(saleDetails?.customer?.phone || '', leftX, y);
        y += 6;
        doc.text(saleDetails?.customer?.email || '', leftX, y);
    }
    // === Table Header ===
    y += 10;
    doc.setFont("helvetica", "bold").setFontSize(10);
    doc.text('Item', leftX, y);
    doc.text('Qty', 120, y, { align: 'right' });
    doc.text('Price', 140, y, { align: 'right' });
    doc.text('Discount', 160, y, { align: 'right' });
    doc.text('Total', 190, y, { align: 'right' });
    y += 2;
    doc.line(leftX, y, 195, y);
    y += 6;

    // === Items List ===
    doc.setFont("helvetica", "normal");
    items.forEach(({ product, quantity, price, discount }, index) => {
        const itemName = `${index + 1}. ${product?.name || 'N/A'}`;
        const itemPrice = Number(price) || 0;
        const itemDiscount = Number(discount) || 0;
        const itemTotal = ((quantity * itemPrice) - itemDiscount).toFixed(2);

        doc.text(itemName, leftX, y);
        doc.text(quantity.toString(), 120, y, { align: 'right' });
        doc.text(itemPrice.toFixed(2), 140, y, { align: 'right' });
        doc.text(itemDiscount.toFixed(2), 160, y, { align: 'right' });
        doc.text(itemTotal, 190, y, { align: 'right' });

        y += 6;
        if (y > 245) {
            doc.addPage();
            y = 20;
        }
    });

    // === Totals Section ===
    y += 2;
    doc.line(leftX, y, 195, y);
    y += 8;

    // === Payment Method Section ===
    doc.setFont("helvetica", "bold").setFontSize(10);
    doc.text('Payment Method:', leftX, y);
    doc.setFont("helvetica", "normal");
    doc.text(saleDetails?.payment_method || 'N/A', leftX + 40, y);
    y += 10;

    doc.setFont("helvetica", "bold");
    doc.text('Subtotal:', 160, y, { align: 'right' });
    doc.text(Number(totalPrice).toFixed(2), 190, y, { align: 'right' });
    y += 6;

    doc.text('Discount:', 160, y, { align: 'right' });
    doc.text(Number(totalDiscount).toFixed(2), 190, y, { align: 'right' });
    y += 6;

    doc.setFontSize(12);
    doc.text('Total:', 160, y, { align: 'right' });
    doc.text((totalPrice - totalDiscount).toFixed(2), 190, y, { align: 'right' });

    // === Footer: Powered by Sell Smart ===
    const footerY = pageHeight - 20;
    doc.setDrawColor(200);
    doc.line(leftX, footerY - 5, 195, footerY - 5);

    if (sellSmartLogo) {
        doc.addImage(sellSmartLogo, 'PNG', leftX, footerY - 2, 10, 10);
    }
    //doc.setFont("helvetica", "normal").setFontSize(8).setTextColor(0, 0, 0);
    //doc.text('Powered by:', leftX + 14, footerY + 2);
    doc.setFont("helvetica", "bold").setFontSize(10).setTextColor(0, 0, 0);
    doc.text('Sell Smart', leftX + 14, footerY + 2);
    doc.setFont("helvetica", "normal").setFontSize(8);
    doc.text('www.sellsmart.co.tz | +255 xxxxxxxx | support@sellsmart.co.tz', leftX + 14, footerY + 6);

    // Save PDF
    const receiptNumber = saleDetails?.invoice_number || saleDetails?.receipt_number || 'Unknown';
    doc.save(`${saleDetails?.type === 'invoice' || saleDetails?.type === 'proforma'  ? `Invoice_${receiptNumber}.pdf` : `Receipt_${receiptNumber}.pdf`}`);
};

export default generateInvoice;
